/**
 * 새 프로젝트 마법사의 단계 검증 규칙 — 정본.
 *
 * 각 단계는 "그 단계를 떠나려면 만족해야 하는 규칙"을 순서 있는 목록으로 선언한다.
 * NEXT 는 현재 단계의 규칙을, CREATE 는 마지막 단계까지의 규칙 전부를 **같은 표**에서
 * 구동한다 — 규칙이 두 검증에 따로 손배선되면 한쪽에만 있는 규칙이 생긴다(베타 3차 M-2 #513:
 * 스코프 커버리지가 CREATE 에만 있어 미할당 계통인 채로 NEXT 가 4단계에 넘어갔다).
 * 새 규칙은 이 표에 한 줄 추가로 끝나야 한다.
 */

import type { WizardStep, WizardFormData } from './types';
import { coveringScopes, scopeShortLabel, uncoveredScopes, validateModelScope } from '@/types/scope';
import { validateName } from '@/domain/namePolicy';
import { partitionLabel } from './types';

export interface WizardRuleContext {
  /** 중복 차단용 기존 프로젝트 이름 목록 — 정규화 비교(규칙은 namePolicy) (#282) */
  existingTitles: readonly string[];
}

/** 위반이면 사용자에게 보일 메시지, 만족이면 null */
export type WizardRule = (data: WizardFormData, ctx: WizardRuleContext) => string | null;

/** 프로젝트 이름 — 규칙은 namePolicy 한 곳(필수·길이·워크스페이스 안 유일) */
const titleValid: WizardRule = (d, ctx) => validateName('project', d.title, ctx.existingTitles);
const scopeNonEmpty: WizardRule = (d) => (d.scope.length > 0 ? null : 'Project Scope를 최소 1개 이상 선택해주세요.');
const partitionsNonEmpty: WizardRule = (d) => (d.partitions.length > 0 ? null : 'Partition을 최소 1개 이상 추가해주세요.');
/** 파티션 이름 = 만들어질 모델의 이름 — 새 모델 창과 같은 규칙(namePolicy: 필수·길이·프로젝트 안 유일, 다른 파티션과 비교) */
const partitionNames: WizardRule = (d) => {
  for (const [i, p] of d.partitions.entries()) {
    const others = d.partitions.filter((o) => o.id !== p.id).map((o) => o.name);
    const problem = validateName('model', p.name, others);
    if (problem) return `${partitionLabel(p, i)}: ${problem}`;
  }
  return null;
};
/** 파티션 scope 규칙 — SSOT = validateModelScope(새 모델·편집 대화상자와 공유): 계통 모델은 담당 필수·프로젝트 scope 안, 제어기·시나리오는 자유 */
const partitionScopes: WizardRule = (d) => {
  for (const p of d.partitions) {
    const err = validateModelScope(p.role, p.scope, d.scope);
    if (err) return `"${p.name}": ${err}`;
  }
  return null;
};
/** 스코프 커버리지 — 계층 규칙(그룹 전체 담당 = 그 계통들도 담당), 계통 모델만 집계 */
const scopeCoverage: WizardRule = (d) => {
  const unassigned = uncoveredScopes(d.scope, coveringScopes(d.partitions));
  return unassigned.length > 0
    ? `다음 Scope가 계통 모델에 할당되지 않았습니다: ${unassigned.map(scopeShortLabel).join(', ')}`
    : null;
};
/** MARS RESTART: 원본 프로젝트·모델·실행까지 필수 — 재시작의 기준선은 원본 실행이 남긴 짝에서 나온다 */
const restartSourceComplete: WizardRule = (d) => {
  for (const p of d.partitions) {
    if (p.analysisCode !== 'MARS' || p.marsConfig?.problemType !== 'RESTART') continue;
    const source = p.marsConfig.restartSource;
    if (!source?.projectId || !source?.modelId) {
      return `"${p.name}" RESTART 파티션의 Source Project와 Model을 선택해주세요.`;
    }
    if (!source.simulationId) {
      return `"${p.name}" RESTART 파티션의 재시작할 원본 실행을 선택해주세요.`;
    }
  }
  return null;
};

/** 단계 → 그 단계를 떠나기 위한 규칙(순서 있음: 첫 위반의 메시지를 보인다) */
export const WIZARD_STEP_RULES: Readonly<Record<WizardStep, readonly WizardRule[]>> = {
  1: [titleValid],
  2: [scopeNonEmpty],
  3: [partitionsNonEmpty, partitionNames, partitionScopes, scopeCoverage],
  4: [restartSourceComplete],
};

/** 현재 단계를 떠날 수 있는가(NEXT) — 첫 위반 메시지 또는 null */
export function stepError(step: WizardStep, data: WizardFormData, ctx: WizardRuleContext): string | null {
  for (const rule of WIZARD_STEP_RULES[step]) {
    const err = rule(data, ctx);
    if (err) return err;
  }
  return null;
}

/** 생성할 수 있는가(CREATE) — 마지막 단계까지의 규칙 전부, 단계 순서대로 */
export function createError(lastStep: WizardStep, data: WizardFormData, ctx: WizardRuleContext): string | null {
  for (let step = 1; step <= lastStep; step += 1) {
    const err = stepError(step as WizardStep, data, ctx);
    if (err) return err;
  }
  return null;
}
